const RETRYABLE = new Set(["RateLimited", "DependencyUnavailable", "Timeout"]);
/** An error from the platform, in the shared envelope (ADR-0001 section 2), or a network failure shaped like one. */
export class ApiError extends Error {
    status;
    code;
    category;
    details;
    requestId;
    constructor(init) {
        super(init.message);
        this.name = "ApiError";
        this.status = init.status;
        this.code = init.code;
        this.category = init.category;
        this.details = init.details ?? [];
        this.requestId = init.requestId ?? null;
    }
    get retryable() { return RETRYABLE.has(this.category); }
}
/** A fresh key for one logical action. Keys use only characters the server accepts (8 to 128 of A-Z a-z 0-9 . _ : -). */
export const newIdempotencyKey = () => `ui-${globalThis.crypto.randomUUID()}`;
const CATEGORY_BY_STATUS = {
    400: "ValidationFailure", 401: "AuthenticationFailure", 403: "AuthorizationFailure", 404: "NotFound", 409: "Conflict",
    422: "BusinessRuleFailure", 429: "RateLimited", 500: "Internal", 502: "DependencyUnavailable", 503: "DependencyUnavailable", 504: "Timeout",
};
export function createApiClient(o) {
    const base = (o.baseUrl ?? "").replace(/\/+$/, "");
    const doFetch = o.fetchImpl ?? ((...a) => fetch(...a));
    const timeoutMs = o.timeoutMs ?? 20_000;
    async function send(method, path, body, opts) {
        const headers = { accept: "application/json" };
        const token = o.getToken();
        if (token)
            headers.authorization = `Bearer ${token}`;
        if (body !== undefined)
            headers["content-type"] = "application/json";
        if (opts.idempotencyKey)
            headers["idempotency-key"] = opts.idempotencyKey;
        const ac = new AbortController();
        let timedOut = false;
        const timer = setTimeout(() => { timedOut = true; ac.abort(); }, timeoutMs);
        const onCallerAbort = () => ac.abort();
        if (opts.signal?.aborted)
            ac.abort();
        else
            opts.signal?.addEventListener("abort", onCallerAbort, { once: true });
        try {
            return await doFetch(base + path, { method, headers, signal: ac.signal, ...(body !== undefined && { body: JSON.stringify(body) }) });
        }
        catch (e) {
            // A caller who cancelled (navigated away) gets the abort back untouched, so it can ignore it quietly.
            if (opts.signal?.aborted)
                throw e;
            throw timedOut
                ? new ApiError({ status: 0, code: "timeout", category: "Timeout", message: "The server took too long to answer. Try again." })
                : new ApiError({ status: 0, code: "unreachable", category: "DependencyUnavailable", message: "Could not reach the server. Check your connection and try again." });
        }
        finally {
            clearTimeout(timer);
            opts.signal?.removeEventListener("abort", onCallerAbort);
        }
    }
    async function toError(res) {
        const requestId = res.headers.get("x-request-id");
        let env;
        try {
            env = await res.json();
        }
        catch {
            env = undefined;
        }
        const e = env?.error;
        // Anything that is not the shared envelope (a gateway page, say) is never shown to the user as-is.
        return new ApiError({
            status: res.status,
            code: e?.code ?? `http_${res.status}`,
            category: e?.category ?? CATEGORY_BY_STATUS[res.status] ?? "Internal",
            message: e?.message ?? `The server returned an unexpected response (HTTP ${res.status}).`,
            details: Array.isArray(e?.details) ? e.details : [],
            requestId: e?.request_id ?? requestId,
        });
    }
    async function json(method, path, body, opts) {
        const res = await send(method, path, body, opts);
        if (!res.ok) {
            const err = await toError(res);
            if (res.status === 401)
                o.onUnauthorized?.();
            throw err;
        }
        return (res.status === 204 ? undefined : await res.json());
    }
    return {
        get: (path, opts = {}) => json("GET", path, undefined, opts),
        post: (path, body, opts) => json("POST", path, body, opts),
        put: (path, body, opts = {}) => json("PUT", path, body, opts),
        async blob(path, opts = {}) {
            const res = await send("GET", path, undefined, opts);
            if (!res.ok) {
                const err = await toError(res);
                if (res.status === 401)
                    o.onUnauthorized?.();
                throw err;
            }
            const cd = res.headers.get("content-disposition") ?? "";
            return { blob: await res.blob(), contentType: res.headers.get("content-type") ?? "application/octet-stream", filename: /filename="([^"]+)"/.exec(cd)?.[1] ?? null };
        },
    };
}
/** `?a=1&b=2` from the defined values only. */
export function query(params) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params))
        if (v !== undefined && v !== null && v !== "")
            p.set(k, String(v));
    const s = p.toString();
    return s ? `?${s}` : "";
}
/** Path segments are always encoded, so an id can never change the route. */
export const seg = (v) => encodeURIComponent(v);
